'use client';

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode
} from "react";
import { ApiError, apiFetch, apiRoutes, authHelpers } from "@/lib/api";

const AUTH_TOKEN_KEY = "authToken";

export interface UserProfile {
    document: string | null;
    phone: string | null;
    birthDate: string | null;
    zipcode: string | null;
    city: string | null;
    state: string | null;
    addressLine: string | null;
    addressNumber: string | null;
    district: string | null;
    instagram: string | null;
    facebook: string | null;
    whatsapp: string | null;
    website: string | null;
    address: string | null;
    logoUrl: string | null;
    primaryColor: string | null;
}

/** Field visibility settings - controls which fields are shown in canvas */
export interface FieldVisibility {
    instagram: boolean;
    facebook: boolean;
    whatsapp: boolean;
    website: boolean;
    address: boolean;
    logo: boolean;
}

export const DEFAULT_FIELD_VISIBILITY: FieldVisibility = {
    instagram: true,
    facebook: true,
    whatsapp: true,
    website: true,
    address: true,
    logo: true,
};

export interface EditorPreferences {
    logo: boolean;
    color: boolean;
    instagram: boolean;
    facebook: boolean;
    whatsapp: boolean;
    website: boolean;
    address: boolean;
    skipPopup: boolean;
    /** Persisted logo scale from user's last manual adjustment (0.15 - 3.0) */
    logoScale?: number;
    /** ISO timestamp of when logoScale was last updated */
    logoScaleUpdatedAt?: string;
    /** Field visibility settings - controls which fields are shown in canvas */
    fieldVisibility?: FieldVisibility;
}

export const DEFAULT_EDITOR_PREFERENCES: EditorPreferences = {
    logo: true,
    color: true,
    instagram: true,
    facebook: true,
    whatsapp: true,
    website: true,
    address: true,
    skipPopup: false,
    logoScale: undefined,
    logoScaleUpdatedAt: undefined,
};

export interface UserAccount {
    id: string;
    name: string;
    email: string;
    roles: string[];
    isPremium: boolean;
    lastLoginAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    avatarUrl: string | null;
    downloadsToday: number;
    freeDownloadsToday: number;
    premiumDownloadsToday: number;
    lastDownloadResetAt: string | null;
    downloadLimit: number;
    downloadsBalance: number;
    membership: string;
    currentPlanCode: string | null;
    currentPlanName: string | null;
    planExpiresAt: string | null;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    acceptedTermsAt: string | null;
    acceptedPrivacyAt: string | null;
    registrationIp: string | null;
    registrationUserAgent: string | null;
    /** Origem do cadastro: 'crie_oferta' ou 'direct' */
    registrationSource: string | null;
    profile: UserProfile;
    editorPreferences: EditorPreferences;
}

interface UpdateProfilePayload {
    name: string;
    email: string;
    profile: Partial<UserProfile>;
}

interface UserContextValue {
    user: UserAccount | null;
    token: string | null;
    isLoading: boolean;
    isRefreshing: boolean;
    refresh: () => Promise<void>;
    updateProfile: (payload: UpdateProfilePayload) => Promise<UserAccount>;
    updateAvatar: (file: File) => Promise<UserAccount>;
    updateLogo: (file: File, onProgress?: (progress: number) => void) => Promise<UserAccount>;
    updateContact: (payload: Partial<Pick<UserProfile, "instagram" | "facebook" | "whatsapp" | "website" | "address">>) => Promise<UserAccount>;
    updatePrimaryColor: (color: string) => Promise<UserAccount>;
    setToken: (token: string | null) => void;
    logout: () => void;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

const getStoredToken = () => {
    if (typeof window === "undefined") {
        return null;
    }

    return localStorage.getItem(AUTH_TOKEN_KEY);
};

export function UserProvider({ children }: { children: ReactNode }) {
    const [token, setTokenState] = useState<string | null>(null);
    const [user, setUser] = useState<UserAccount | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const applyToken = useCallback((value: string | null) => {
        if (typeof window === "undefined") {
            setTokenState(value);
            return;
        }

        if (value) {
            // Use authHelpers to save in both localStorage and cookies
            authHelpers.setToken(value);
        } else {
            // Use authHelpers to clear both localStorage and cookies
            authHelpers.clearTokens();
        }

        setTokenState(value);
    }, []);

    const loadUser = useCallback(
        async (activeToken: string) => {
            setIsRefreshing(true);

            try {
                const data = await apiFetch<{ user: UserAccount }>(apiRoutes.user.me(), {
                    method: "GET",
                    token: activeToken,
                    cache: "no-store"
                });

                setUser(data.user);

                // Salvar email no localStorage para verificação de admin
                if (typeof window !== 'undefined' && data.user.email) {
                    localStorage.setItem('userEmail', data.user.email);
                }
            } catch (error) {
                console.error("Não foi possível carregar o perfil do usuário", error);
                if (error instanceof ApiError && error.isTokenError) {
                    // Apenas mensagens explícitas de "Token inválido/expirado/não encontrado"
                    // disparam logout. Um 401 genérico (ex.: race condition durante
                    // hidratação, latência) NÃO deve limpar o token aqui, pois isso
                    // quebra chamadas paralelas (ex.: GET /editor/session) que
                    // acabariam vendo o localStorage vazio e redirecionando para /login.
                    applyToken(null);
                    setUser(null);
                    console.warn("Token inválido, usuário deslogado");
                } else if (error instanceof ApiError && error.status === 401) {
                    // 401 sem indicação clara de token inválido: apenas zera o user,
                    // mantém o token para a próxima tentativa / outras requisições.
                    setUser(null);
                    console.warn("GET /users/me retornou 401, mantendo token para retry");
                }
            } finally {
                setIsRefreshing(false);
            }
        },
        [applyToken]
    );

    const refresh = useCallback(async () => {
        const activeToken = getStoredToken();
        if (!activeToken) {
            setUser(null);
            setTokenState(null);
            return;
        }

        setTokenState(activeToken);
        await loadUser(activeToken);
    }, [loadUser]);

    useEffect(() => {
        if (typeof window === "undefined") {
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        const initialize = async () => {
            const storedToken = getStoredToken();
            if (!storedToken) {
                setIsLoading(false);
                return;
            }

            setTokenState(storedToken);
            await loadUser(storedToken);
            if (!cancelled) {
                setIsLoading(false);
            }
        };

        void initialize();

        const handleTokenEvent = () => {
            void refresh();
        };

        const handleStorage = (event: StorageEvent) => {
            if (event.key === AUTH_TOKEN_KEY) {
                void refresh();
            }
        };

        // Interceptar eventos de autenticação para renovação automática
        const handleAuthTokenChanged = () => {
            const newToken = getStoredToken();
            if (newToken) {
                // React skips re-render if value hasn't changed
                setTokenState(newToken);
            }
        };

        window.addEventListener("auth:token-changed", handleTokenEvent);
        window.addEventListener("auth:token-changed", handleAuthTokenChanged);
        window.addEventListener("storage", handleStorage);

        return () => {
            cancelled = true;
            window.removeEventListener("auth:token-changed", handleTokenEvent);
            window.removeEventListener("auth:token-changed", handleAuthTokenChanged);
            window.removeEventListener("storage", handleStorage);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadUser, refresh]);

    // Reverificacao on-demand do status premium ao retornar do Crie Oferta.
    // O CrieOferta anexa ?crieOfertaUpdated=1 ao returnTo apos pagamento
    // bem-sucedido; aqui detectamos a flag, chamamos o endpoint de refresh
    // e limpamos a query string para evitar loops.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const url = new URL(window.location.href);
        if (url.searchParams.get("crieOfertaUpdated") !== "1") return;
        if (!user || user.registrationSource !== "crie_oferta") return;

        let cancelled = false;
        (async () => {
            try {
                await apiFetch(apiRoutes.user.refreshCrieOferta(), { method: "POST" });
                if (!cancelled) await refresh();
            } catch (err) {
                console.warn("[UserContext] refresh-crie-oferta falhou:", err);
            } finally {
                if (!cancelled) {
                    url.searchParams.delete("crieOfertaUpdated");
                    window.history.replaceState({}, "", url.toString());
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [user, refresh]);

    const updateProfile = useCallback(
        async (payload: UpdateProfilePayload) => {
            const activeToken = getStoredToken();
            if (!activeToken) {
                throw new Error("Usuário não autenticado");
            }

            const data = await apiFetch<{ user: UserAccount }>(apiRoutes.user.me(), {
                method: "PUT",
                token: activeToken,
                body: JSON.stringify(payload)
            });

            setUser(data.user);
            window.dispatchEvent(new CustomEvent("profile:updated"));
            return data.user;
        },
        []
    );

    const updateAvatar = useCallback(async (file: File) => {
        const activeToken = getStoredToken();
        if (!activeToken) {
            throw new Error("Usuário não autenticado");
        }

        const formData = new FormData();
        formData.append("avatar", file);

        const data = await apiFetch<{ user: UserAccount }>(apiRoutes.user.avatar(), {
            method: "PUT",
            token: activeToken,
            body: formData
        });

        setUser(data.user);
        window.dispatchEvent(new CustomEvent("profile:updated"));
        return data.user;
    }, []);

    const updateLogo = useCallback(async (file: File, onProgress?: (progress: number) => void) => {
        // console.log("📸 [UserContext.updateLogo] Iniciando upload de logo em chunks...", {
            // fileName: file.name,
            // fileSize: file.size,
            // fileType: file.type
        // });

        const activeToken = getStoredToken();
        if (!activeToken) {
            console.error("❌ [UserContext.updateLogo] Token não encontrado");
            throw new Error("Usuário não autenticado");
        }

        // console.log("🔑 [UserContext.updateLogo] Token encontrado");

        try {
            // Usar upload em chunks para arquivos maiores que 2MB
            if (file.size > 2 * 1024 * 1024) {
                // console.log("📦 [UserContext.updateLogo] Usando upload em chunks (arquivo > 2MB)");

                const { uploadLogoInChunks } = await import('@/lib/logoChunkUpload');

                const result = await uploadLogoInChunks(file, {
                    onProgress: (progress) => {
                        // console.log(`📊 [UserContext.updateLogo] Progresso: ${Math.round(progress)}%`);
                        onProgress?.(progress);
                    },
                    onChunkComplete: (chunkIndex, totalChunks) => {
                        // console.log(`✅ [UserContext.updateLogo] Chunk ${chunkIndex + 1}/${totalChunks} enviado`);
                    }
                });

                // console.log("✅ [UserContext.updateLogo] Logo atualizada via chunks:", result);

                setUser(result.user);
                window.dispatchEvent(new CustomEvent("profile:updated"));
                return result.user;
            }

            // Upload tradicional para arquivos menores
            // console.log("📤 [UserContext.updateLogo] Usando upload tradicional (arquivo < 2MB)");

            const formData = new FormData();
            formData.append("logo", file);

            const data = await apiFetch<{ user: UserAccount }>(apiRoutes.user.logo(), {
                method: "PUT",
                token: activeToken,
                body: formData
            });

            // console.log("✅ [UserContext.updateLogo] Logo atualizada:", {
                // userId: data.user.id,
                // logoUrl: data.user.profile?.logoUrl
            // });

            setUser(data.user);
            window.dispatchEvent(new CustomEvent("profile:updated"));
            return data.user;
        } catch (error) {
            console.error("❌ [UserContext.updateLogo] Erro na requisição:", error);
            throw error;
        }
    }, []);

    const updateContact = useCallback(
        async (payload: Partial<Pick<UserProfile, "instagram" | "facebook" | "whatsapp" | "website" | "address">>) => {
            const activeToken = getStoredToken();
            if (!activeToken) {
                throw new Error("Usuário não autenticado");
            }

            const data = await apiFetch<{ user: UserAccount }>(apiRoutes.user.contact(), {
                method: "PUT",
                token: activeToken,
                body: JSON.stringify(payload)
            });

            setUser(data.user);
            window.dispatchEvent(new CustomEvent("profile:updated"));
            return data.user;
        },
        []
    );

    const updatePrimaryColor = useCallback(
        async (color: string) => {
            const activeToken = getStoredToken();
            if (!activeToken) {
                throw new Error("Usuário não autenticado");
            }

            const data = await apiFetch<{ user: UserAccount; primaryColor: string }>(apiRoutes.user.primaryColor(), {
                method: "PUT",
                token: activeToken,
                body: JSON.stringify({ color })
            });

            setUser(data.user);
            window.dispatchEvent(new CustomEvent("profile:updated"));
            return data.user;
        },
        []
    );

    const setToken = useCallback(
        (value: string | null) => {
            applyToken(value);
            if (typeof window !== "undefined") {
                window.dispatchEvent(new Event("auth:token-changed"));
            }
        },
        [applyToken]
    );

    const logout = useCallback(() => {
        applyToken(null);
        setUser(null);
        if (typeof window !== "undefined") {
            sessionStorage.removeItem("authUser");
            window.dispatchEvent(new Event("auth:token-changed"));
            window.location.href = "/login";
        }
    }, [applyToken]); useEffect(() => {
        if (!token) {
            setUser(null);
        }
    }, [token]);

    useEffect(() => {
        if (!isLoading && !token) {
            setIsRefreshing(false);
        }
    }, [isLoading, token]);

    const value = useMemo<UserContextValue>(
        () => ({
            user,
            token,
            isLoading,
            isRefreshing,
            refresh,
            updateProfile,
            updateAvatar,
            updateLogo,
            updateContact,
            updatePrimaryColor,
            setToken,
            logout
        }),
        [isLoading, isRefreshing, logout, refresh, token, updateAvatar, updateLogo, updateContact, updatePrimaryColor, updateProfile, user]
    );

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUser deve ser utilizado dentro de UserProvider");
    }
    return context;
};
